import { Card, CardContent, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DashboardProps } from '../SextforceMetricsAccountValueStats';
import ExportButtons from './ExportButtons';

const formatNumber = (num: number): string => {
    return num.toLocaleString();
};

const SubscriberGrowthChart: React.FC<DashboardProps> = ({ subscriber, data, isLoading }) => {
    const theme = useTheme();

    if (isLoading) {
        return (
            <Card elevation={3}>
                <CardContent>
                    <Skeleton variant="text" width={200} />
                    <Skeleton variant="rectangular" width="100%" height={300} />
                </CardContent>
            </Card>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Card elevation={3}>
                <CardContent>
                    <Stack direction="column" spacing={1} mb={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Typography variant="h6" gutterBottom>
                                Subscriber Growth (Last 30 Days)
                            </Typography>
                        </Stack>
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                        No data available for subscriber growth.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Get the last 30 days data
    const recentData = data.slice(-30);

    // Map the subscriber data using moment
    const subscriberData = recentData.map(day => {
        const dayDate = moment(day._id);
        return {
            date: dayDate.format('YYYY-MM-DD'),
            dateFormatted: dayDate.format('MMM D'),
            active: day.counters?.subscribers.active || 0,
            new: day.fansSubscriptions?.fans?.subscriptions?.new?.total || 0,
            renew: day.fansSubscriptions?.fans?.subscriptions?.renew?.total || 0,
        };
    });

    // Sort the data by date
    subscriberData.sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());

    return (
        <Card elevation={3}>
            <CardContent>
                <Stack direction="column" spacing={1} mb={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="h6" gutterBottom>
                            Subscriber Growth (Last 30 Days)
                        </Typography>
                        <ExportButtons
                            data={subscriberData}
                            componentType="subscriberGrowth"
                            filename={`${subscriber.username}_Subscriber_Growth`}
                        />
                    </Stack>
                    <Typography variant="body2" color="textSecondary">
                        {moment().subtract(30, 'days').format('MMM D, YYYY')} - {moment().format('MMM D, YYYY')}
                    </Typography>
                </Stack>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={subscriberData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="dateFormatted" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
                        <YAxis
                            tick={{
                                fontSize: 11,
                            }}
                        />
                        <Tooltip
                            labelFormatter={label => `Date: ${label}`}
                            formatter={value => [`${formatNumber(value as number)} subscribers`, undefined]}
                        />
                        <Legend verticalAlign="top" align="right" />
                        <Bar dataKey="new" name="New Subscribers" fill={theme.palette.success.main} stackId="a" />
                        <Bar dataKey="renew" name="Renewals" fill={theme.palette.info.main} stackId="a" />
                    </BarChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
};

export default SubscriberGrowthChart;
